import { format, formatDistanceToNowStrict, parse, subYears } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

export const toFriendlyDate = (date: number | string | null | undefined) => {
  if (!date) return '-';

  const articleTime =
    typeof date === 'string'
      ? zonedTimeToUtc(date, 'America/Chicago').getTime()
      : date * 1000;

  const currentDate = new Date();
  const articleDate = new Date(articleTime);

  const msInADay = 24 * 60 * 60 * 1000;
  const timeDifference = currentDate.getTime() - articleDate.getTime();

  return timeDifference <= msInADay
    ? formatDistanceToNowStrict(articleDate, { addSuffix: true })
    : format(new Date(articleDate), 'MM/dd/yy');
};

export const toTimeDistance = (
  date: number | Date,
  abbreviated?: boolean,
  addSuffix?: boolean,
) => {
  const formatDistanceLocaleAbbr: Record<string, (count: number) => string> = {
    xSeconds: (c: number) => `${c}s`,
    halfAMinute: () => '30s',
    xMinutes: (c: number) => `${c}m`,
    xHours: (c: number) => `${c}h`,
    xDays: (c: number) => `${c}d`,
    xWeeks: (c: number) => `${c}w`,
    xMonths: (c: number) => `${c}mo`,
    xYears: (c: number) => `${c}yr`,
  };

  const locale = abbreviated
    ? {
        formatDistance: (token: string, count: number) =>
          formatDistanceLocaleAbbr[token](count),
      }
    : undefined;

  return formatDistanceToNowStrict(date, { locale, addSuffix });
};

//** monthsBack is how many months back you need start and end date for */
export const getPreviousMonthDates = (
  monthsBack = 1,
): {
  startDate: string;
  endDate: string;
} => {
  const today = new Date();
  const previousMonth = new Date(
    today.getFullYear(),
    today.getMonth() - monthsBack,
    1,
  );
  const year = previousMonth.getFullYear();
  const month = String(previousMonth.getMonth() + 1).padStart(2, '0');
  const firstDay = '01';
  const lastDay = new Date(year, previousMonth.getMonth() + 1, 0)
    .getDate()
    .toString();

  return {
    startDate: `${year}-${month}-${firstDay}`,
    endDate: `${year}-${month}-${lastDay}`,
  };
};

const getPreviousQuarterDates = (): { startDate: string; endDate: string } => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  let startMonth: number;
  let endMonth: number;

  if (currentMonth >= 0 && currentMonth <= 2) {
    startMonth = 9;
    endMonth = 11;
  } else if (currentMonth >= 3 && currentMonth <= 5) {
    startMonth = 0;
    endMonth = 2;
  } else if (currentMonth >= 6 && currentMonth <= 8) {
    startMonth = 3;
    endMonth = 5;
  } else {
    startMonth = 6;
    endMonth = 8;
  }

  const startDate = `${currentYear}-${String(startMonth + 1).padStart(
    2,
    '0',
  )}-01`;
  const endDate = `${currentYear}-${String(endMonth + 1).padStart(
    2,
    '0',
  )}-${new Date(currentYear, endMonth + 1, 0).getDate()}`;

  return { startDate, endDate };
};

const getComparePreviousQuarterDates = (): {
  startDate: string;
  endDate: string;
} => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  let startMonth: number;
  let endMonth: number;
  let adjustedYear: number;

  if (currentMonth >= 0 && currentMonth <= 2) {
    startMonth = 6;
    endMonth = 8;
    adjustedYear = currentYear - 1; // Adjust the year for two quarters back
  } else if (currentMonth >= 3 && currentMonth <= 5) {
    startMonth = 9;
    endMonth = 11;
    adjustedYear = currentYear - 1; // Adjust the year for two quarters back
  } else if (currentMonth >= 6 && currentMonth <= 8) {
    startMonth = 0;
    endMonth = 2;
    adjustedYear = currentYear;
  } else {
    startMonth = 3;
    endMonth = 5;
    adjustedYear = currentYear;
  }

  const startDate = `${adjustedYear}-${String(startMonth + 1).padStart(
    2,
    '0',
  )}-01`;
  const endDate = `${adjustedYear}-${String(endMonth + 1).padStart(
    2,
    '0',
  )}-${new Date(adjustedYear, endMonth + 1, 0).getDate()}`;

  return { startDate, endDate };
};

const getPreviousYearDates = (): { startDate: string; endDate: string } => {
  const currentDate = new Date();
  const previousYear = currentDate.getFullYear() - 1;
  const startDate = `${previousYear}-01-01`;
  const endDate = `${previousYear}-12-31`;

  return { startDate, endDate };
};

export const subtractYearFromDates = (dates: {
  startDate: string;
  endDate: string;
}): { startDate: string; endDate: string } => {
  const startDate = parse(dates.startDate, 'yyyy-M-d', new Date());
  const endDate = parse(dates.endDate, 'yyyy-M-d', new Date());

  const newStartDate = subYears(new Date(startDate), 1);
  const newEndDate = subYears(new Date(endDate), 1);

  return {
    startDate: newStartDate.toISOString().split('T')[0],
    endDate: newEndDate.toISOString().split('T')[0],
  };
};

export const getMonthSpan = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const yearDiff = end.getFullYear() - start.getFullYear();
  const monthDiff = end.getMonth() - start.getMonth();

  return yearDiff * 12 + monthDiff;
};

export const getCustomPeriodDates = (
  startDate: string,
  monthSpan: number,
): { startDate: string; endDate: string } => {
  const adjustedStartDate = new Date(startDate);

  adjustedStartDate.setMonth(adjustedStartDate.getMonth() - monthSpan + 1);
  adjustedStartDate.setDate(1);

  const adjustedEndDate = new Date(startDate);
  const twoDigitMonth = (adjustedStartDate.getMonth() + 1).toLocaleString(
    'en-US',
    { minimumIntegerDigits: 2 },
  );

  adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);

  return {
    startDate: `${adjustedStartDate.getFullYear()}-${twoDigitMonth}-01`,
    endDate: adjustedEndDate.toISOString().split('T')[0],
  };
};

export const getUserTimeZone = (
  zoneFormat: Intl.DateTimeFormatOptions['timeZoneName'] = 'long' as keyof Intl.DateTimeFormatOptions['timeZoneName'],
) => {
  const date = new Date();
  const userTimeZone = Intl.DateTimeFormat(undefined, {
    timeZoneName: zoneFormat,
  }).formatToParts(date);
  const timeZoneName = userTimeZone.find(
    (part) => part.type === 'timeZoneName',
  );

  if (timeZoneName && zoneFormat === 'long') {
    // Remove "Daylight" and "Time" from the string
    const cleanedTimeZoneName = timeZoneName.value
      .replace(/(Daylight|Time)/g, '')
      .trim();

    return cleanedTimeZoneName;
  } else if (timeZoneName) {
    return timeZoneName.value;
  }

  return '';
};

export const comparePreviousQuarterDates = getComparePreviousQuarterDates();
export const previousQuarterDates = getPreviousQuarterDates();
export const previousYearDates = getPreviousYearDates();

export const yearOptions = [
  ...Array.from({ length: 25 }, (_, i) => {
    const year = new Date().getFullYear() - i;

    return { label: year.toString(), value: year.toString() };
  }),
];

/**
 * Converts a date field's value to the correct iso string for the api
 */
export const toDateIsoString = (date: string | null | undefined): string => {
  return date ? new Date(date).toISOString() : '';
};

/**
 * Converts an iso string date from the api to a parsed data field value
 */
export const fromDateIsoString = (date: string | null | undefined): string => {
  return date ? format(new Date(date), "yyyy-MM-dd'T'HH:mm") : '';
};
